import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router";

import Input from "../../../../../../components/Form/Input/input";
import { TextArea } from "../../../../../../components/Form/Input/textArea";
import Label from "../../../../../../components/Form/Label";
import { Label as LabelText } from "../../../../../../components/Form/Label/styles";
import { Form, FormGroup } from "../../../../../../components/Form/styles";
import { ImageField } from "../../../../../../components/ImageField";
import { Box } from "../../../../../../components/Layout";
import SaveBar from "../../../../../../components/SaveBar";
import { SubHeader } from "../../../../../../components/SubHeader";
import { CropProps } from "../../../../../../components/UploadImageInput";
import { withPage } from "../../../../../../hooks/CurrentPage";
import { useQueryUrl } from "../../../../../../hooks/QueryUrl";
import { TESTIMONIAL_MAX_NAME, TESTIMONIAL_MAX_TEXT, TestimonialItem } from "../../../../../../model/site";
import { clone, deepEqual } from "../../../../../../utils/objects";
import { uuidv4 } from "../../../../../../utils/uuid";
import { useItemPreview } from "../../../../hooks/Section";
import { navigationState } from "../../../Sections/navigationState";
import { TestimonialsNavigationState } from "../../testimonialsNavigationState";
import { Container, Field, Stars } from "./styles";

const prefix = "pages.editor.testimonials.item";

const MAX_RATING = 5;

// The photo shows in a circle on the page: cropped square, picked in a round frame
const photoCrop: CropProps = {
  slice: { aspect: 1 },
  size: { width: 400, height: 400 },
  cropShape: "round",
};

const Star = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 2l2.9 6.3 6.9.8-5.1 4.7 1.4 6.8L12 17.3 5.9 20.6l1.4-6.8L2.2 9.1l6.9-.8L12 2z" />
  </svg>
);

const ItemTestimonial = ({ state }: { state: TestimonialsNavigationState }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const headerTitle = useRef(t(`${prefix}.header.title.${state.editingTestimonial?.id ? "edit" : "add"}`));
  const initialItemState = useRef<TestimonialItem>(
    clone<TestimonialItem>(state.editingTestimonial || { id: uuidv4(), text: "", name: "", rating: MAX_RATING })
  );

  const [item, setItem] = useState(initialItemState.current);

  useItemPreview(state.currentSection, item, existing => existing.id === item.id);

  const isSaveDisabled = !item.text.trim() || !item.name.trim();
  const hasUnsavedChanges = () => !deepEqual(initialItemState.current, item);

  const saveHandler = () => {
    const items = state.currentSection.items || [];
    const index = items.findIndex(testimonial => testimonial.id === initialItemState.current.id);

    if (index >= 0) {
      items[index] = item;
    } else {
      items.push(item);
    }

    state.currentSection.items = items;
    navigate(-1);
  };

  const removePhoto = () => {
    setItem(current => {
      const next = { ...current };
      delete next.image;
      return next;
    });
  };

  return (
    <Container>
      <SubHeader
        title={headerTitle.current}
        crumbs={[
          { label: t("pages.editor.preview.footer.actions.sections"), to: "/editor/sections" },
          { label: t("pages.editor.testimonials.header.title"), to: "/editor/testimonials" },
        ]}
        hasUnsavedChanges={hasUnsavedChanges}
      />
      <Box>
        <Form onSubmit={event => event.preventDefault()}>
          <FormGroup>
            <TextArea
              id="text"
              required
              style={{ height: "8rem" }}
              maxLength={TESTIMONIAL_MAX_TEXT}
              autoCapitalize="on"
              value={item.text}
              onChange={e => setItem({ ...item, text: e.target.value })}
            />
            <Label
              htmlFor="text"
              maxLength={TESTIMONIAL_MAX_TEXT}
              usedLength={item.text.length}
              required
              text={t(`${prefix}.form.text`)}
            />
          </FormGroup>
          <FormGroup>
            <Input
              id="name"
              required
              type="text"
              value={item.name}
              autoCapitalize="words"
              maxLength={TESTIMONIAL_MAX_NAME}
              onChange={e => setItem({ ...item, name: e.target.value })}
              label={t(`${prefix}.form.name`)}
            />
          </FormGroup>

          <Field>
            <LabelText as="span" className="caption" id="rating-caption">
              {t(`${prefix}.form.rating`)}
            </LabelText>
            <Stars role="radiogroup" aria-labelledby="rating-caption">
              {Array.from({ length: MAX_RATING }, (_, index) => index + 1).map(stars => (
                <button
                  type="button"
                  key={stars}
                  role="radio"
                  className={stars <= item.rating ? "on" : undefined}
                  aria-checked={stars === item.rating}
                  aria-label={t(`${prefix}.form.${stars === 1 ? "starsOne" : "starsMany"}`, { count: stars })}
                  onClick={() => setItem({ ...item, rating: stars })}>
                  <Star />
                </button>
              ))}
            </Stars>
          </Field>

          <Field>
            <ImageField
              shape="round"
              crop={photoCrop}
              title={t(`${prefix}.form.photo`)}
              hint={t(`${prefix}.form.photoHint`)}
              image={item.image}
              addLabel={t(`${prefix}.form.addPhoto`)}
              changeLabel={t(`${prefix}.form.changePhoto`)}
              onChange={image => (image ? setItem(current => ({ ...current, image })) : removePhoto())}
            />
          </Field>
        </Form>
      </Box>
      <SaveBar onSave={saveHandler} disabled={isSaveDisabled} hasUnsavedChanges={hasUnsavedChanges} />
    </Container>
  );
};

/** The screen of one testimonial. Reached from the list of the section, which hands it the section. */
const ItemTestimonialPage = () => {
  const state = navigationState.get() as TestimonialsNavigationState | undefined;
  const page = useQueryUrl().get("page");

  // opened by its address (a reload): there's no section to add the testimonial to
  if (state?.currentSection?.type !== "testimonials") {
    return <Navigate to={withPage("/editor/sections", page)} replace />;
  }
  return <ItemTestimonial state={state} />;
};

export default ItemTestimonialPage;
